import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
import { ContextAccess } from '@shared/Root/Context.access';

// Presence part of the concurrent editing detection in IssueEditorViewModel / SurveysViewModel (no view mode / take over).
// Talks to ObjectTrackingHub through the global objectTracker (Scripts/objectTracking.js, loaded by the signalr bundle).

declare var $: any;
declare var objectTracker: any;
declare var SignalRHubFactory: any;

interface ActiveConnection {
    ConnectionId: string;
    UserId: string;
    UserName: string;
    Name: string;
}

export interface ActiveEditor {
    userId: string;
    userName: string;
    name: string;
}

export interface OthersJoined {
    otherEditors: ActiveEditor[];
    otherTabsCount: number;
}

const HEARTBEAT_INTERVAL_MS = 60000;

export function useObjectTracking(objectId: () => string | null | undefined, onOthersJoined?: (others: OthersJoined) => void) {
    const contextAccess: ContextAccess = inject(injectorAccessInjectionKey)!.injector.get(ContextAccess);
    const currentUser = computed(() => contextAccess.state.currentUser);

    const connections = ref<ActiveConnection[]>([]);
    const ownConnectionId = ref<string | null>(null);

    const otherConnections = computed(() => connections.value.filter((c) => c.ConnectionId !== ownConnectionId.value));
    const isOpenByOthers = computed(() => otherConnections.value.length > 0);
    const otherTabsCount = computed(() => otherConnections.value.filter((c) => c.UserId === currentUser.value?.UserId).length);
    const otherEditors = computed<ActiveEditor[]>(() => {
        const editors = new Map<string, ActiveEditor>();
        otherConnections.value
            .filter((c) => c.UserId !== currentUser.value?.UserId)
            .forEach((c) => editors.set(c.UserId, { userId: c.UserId, userName: c.UserName, name: c.Name }));
        return [...editors.values()];
    });

    // Notify only for connections not seen before, so every presence update doesn't re-notify.
    let seenConnectionIds = new Set<string>();
    watch(otherConnections, (others) => {
        const joined = others.some((c) => !seenConnectionIds.has(c.ConnectionId));
        seenConnectionIds = new Set(others.map((c) => c.ConnectionId));
        if (joined) onOthersJoined?.({ otherEditors: otherEditors.value, otherTabsCount: otherTabsCount.value });
    });

    let hub: any = null;
    let connection: any = null;
    let heartbeat: number | undefined;
    let announcedObjectId: string | null = null;
    let announcedConnectionId: string | null = null;
    let unmounted = false;

    function onActiveConnectionsUpdate(active: ActiveConnection[]) {
        ownConnectionId.value = SignalRHubFactory.validateConnection().connectionId;
        connections.value = active;
    }

    // Bound directly on the connection: connection.stateChanged() wraps the callback and cannot be unbound.
    function onStateChanged(_: unknown, change: { newState: number }) {
        if (change.newState === $.signalR.connectionState.connected) announce();
    }

    function announce() {
        const id = objectId();
        const user = currentUser.value;
        const status = SignalRHubFactory.validateConnection();
        if (!hub || !id || !user || !status.isValid) return;
        if (announcedObjectId === id && announcedConnectionId === status.connectionId) return;

        announcedObjectId = id;
        announcedConnectionId = status.connectionId;
        ownConnectionId.value = status.connectionId;
        hub.objectOpened(id, user.Name);
    }

    function close() {
        if (!hub || !announcedObjectId) return;
        if (SignalRHubFactory.validateConnection().isValid) hub.invoke('ObjectClosed', announcedObjectId).catch(() => {});
        objectTracker.setCurrentObjectId(null);
        announcedObjectId = null;
        announcedConnectionId = null;
        connections.value = [];
    }

    onMounted(async () => {
        try {
            hub = await objectTracker.ensureInitialized();
        } catch (error) {
            SignalRHubFactory.handleError('objectTracking_vue_initialization', error, { objectId: objectId() });
            return;
        }
        if (unmounted) return;

        connection = SignalRHubFactory.getConnection();
        $(connection).on('onStateChanged', onStateChanged);
        hub.on('activeConnectionsUpdate', onActiveConnectionsUpdate);

        heartbeat = window.setInterval(() => {
            if (!announcedObjectId || !SignalRHubFactory.validateConnection().isValid) return;
            hub.objectActive(announcedObjectId, currentUser.value?.Name ?? '');
        }, HEARTBEAT_INTERVAL_MS);

        announce();
    });

    // The current user may still be loading when mounted.
    watch(currentUser, announce);

    watch(objectId, () => {
        close();
        announce();
    });

    onBeforeUnmount(() => {
        unmounted = true;
        window.clearInterval(heartbeat);
        if (connection) $(connection).off('onStateChanged', onStateChanged);
        if (hub) hub.off('activeConnectionsUpdate', onActiveConnectionsUpdate);
        close();
    });

    return {
        isOpenByOthers,
        otherTabsCount,
        otherEditors,
    };
}
